import  {React, useRef, useState, useEffect, useCallback } from "react";
import LocationService from "../../components/Services/LocationService";
import leadSourceService from "../../components/Services/LeadSourceService";
import InterestedTagService from "../../components/Services/InterestedTagService";
import { showErrorToast, showSuccessToast } from "../../components/Constant/Notification";

import * as xlsx from "xlsx";


const useInterestAndSource = () => {
  // Data states
  const [interestLocationData, setInterestLocationData] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [interestTagData, setInterestTagData] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI states
  const [menuOpen, setMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalHeading, setModalHeading] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [inputJson, setInputJson] = useState(null);
  
  const [insertedCount , setInsertedCount] = useState(0);
  const [skippedCount , setSkippedCount] = useState(0);
  const [skippedUnitsInfo, setSkippedUnitsInfo] = useState({
    skippedCount: 0,
    skippedUnitNos: []
  });
  const [showSkippedModal, setShowSkippedModal] = useState(false);

 
  const dropdownRef = useRef();


  const [tagLimits, setTagLimits] = useState({
    interestTag: 250,
    leadSource:  250,
    location:  250
  });

  const [counts, setCounts] = useState({
    interestTag: 0,
    leadSource: 0,
    location: 0
  });

  const [limitsExceeded, setLimitsExceeded] = useState({
    interestTag: false,
    leadSource: false,
    location: false
  });


  useEffect(() => {
    setLimitsExceeded({
      interestTag: counts.interestTag >= tagLimits.interestTag,
      leadSource: counts.leadSource >= tagLimits.leadSource,
      location: counts.location >= tagLimits.location
    });
  }, [counts, tagLimits]);


  const toggleMenu = useCallback(() => {
    setMenuOpen(prev => !prev);
  }, []);

 
  const openModal = useCallback((heading) => {
    const limitKey = heading === "Interest Tags" ? "interestTag" :
                    heading === "Lead Sources" ? "leadSource" :
                    heading === "Location" ? "location" : "";
    
    if (limitsExceeded[limitKey]) {
      showErrorToast(`You've reached the maximum limit of ${tagLimits[limitKey]} ${heading.toLowerCase()}`);
      return;
    }
    
    setModalHeading(heading);
    setIsModalOpen(true);
    setMenuOpen(false);
  }, [limitsExceeded, tagLimits]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setInputJson(null);
  }, []);

  // Fetch functions
  const fetchInterestLocationData = async () => {
    setLoading(true);
    try {
      const response = await LocationService.fetchLocation();
      if (response.status === 204) {
        setInterestLocationData([]);
        setCounts(prev => ({ ...prev, location: 0 }));
      } else {
        const data = response.data;
        const transformedData = Object.entries(data)?.map(([name, id]) => ({
          name,
          id,
        }));
        setInterestLocationData(transformedData);
        setCounts(prev => ({ ...prev, location: transformedData.length }));
      }
    } catch (error) {
      console.error("Error fetching location data:", error);
      showErrorToast("Error fetching location data");
    } finally {
      setLoading(false);
    }
  }

  const fetchSourceData = async () => {
    setLoading(true);
    try {
      const response = await leadSourceService.fetchLeadSources();
      if (response.status === 204) {
        setSourceData([]);
        setCounts(prev => ({ ...prev, leadSource: 0 }));
      } else {
        setSourceData(response.data);
        setCounts(prev => ({ ...prev, leadSource: response.data.length }));
      }
    } catch (error) {
      console.error("Error fetching source data:", error);
      showErrorToast("Error fetching source data");
    } finally {
      setLoading(false);
    }
  }

  const fetchInterestTagData = async () => {
    setLoading(true);
    try {
      const response = await InterestedTagService.fetchInterestedTag();
      console.log(response)
      if (response.status === 204) {
        setInterestTagData([]);
        setCounts(prev => ({ ...prev, interestTag: 0 }));
      } else {
        setInterestTagData(response.data);
        setCounts(prev => ({ ...prev, interestTag: response.data.length }));
       
      }

    } catch (error) {
      console.error("Error fetching interest tag data:", error);
      showErrorToast("Error fetching interest tag data");
    } finally {
      setLoading(false);
    }
  }

  
//   const fetchAllData = useCallback(async () => {
//     // await Promise.all([
//     //   fetchInterestLocationData(),
//     //   fetchSourceData(),
//     //   fetchInterestTagData()
//     // ]);
//   }, [fetchInterestLocationData, fetchSourceData, fetchInterestTagData]);

//   useEffect(() => {
//     fetchInterestLocationData();
//     fetchSourceData();
//     fetchInterestTagData();
//   },[fetchInterestLocationData, fetchSourceData, fetchInterestTagData]);

  const createInterestLocation = async (data) => {
    if (!data || data.trim() === "") {
      showErrorToast("Location name cannot be empty");
      return;
    }
    try {
      await LocationService.createLocation(data.trim());
      await fetchInterestLocationData();
      showSuccessToast("Location created successfully");
    } catch (error) {
      console.error("Error creating interest location:", error);
      showErrorToast(error.response?.data || "Error creating location");
    }
  }

  const createSource = async (data) => {
    if (!data || data.trim() === "") {
      showErrorToast("Source name cannot be empty");
      return;
    }
    try {
      await leadSourceService.addLeadSource(data.trim());
      await fetchSourceData();
      showSuccessToast("Lead source created successfully");
    } catch (error) {
      console.error("Error creating source:", error);
      showErrorToast(error.response?.data || "Error creating lead source");
    }
  };

  const createInterestTag = async (data) => {
    if (!data || data.trim() === "") {
      showErrorToast("Interest tag name cannot be empty");
      return;
    }
    try {
      await InterestedTagService.createInterestedTag(data.trim());
      await fetchInterestTagData();
      showSuccessToast("Interest tag created successfully");
    } catch (error) {
      console.error("Error creating interest tag:", error);
      showErrorToast(error.response?.data || "Error creating interest tag");
    }
  }

  // Update functions
  const updateInterestLocation = async (id, data) => {
    try {
      await LocationService.updateLocation(id, data.trim());
      await fetchInterestLocationData();
      showSuccessToast("Location updated successfully");
    } catch (error) {
      console.error("Error updating interest location:", error);
      showErrorToast(error.response?.data || "Error updating location");
    }
  }

  const updateSource = async (id, data) => {
    try {
      await leadSourceService.updateLeadSource(id, data.trim());
      await fetchSourceData();
      showSuccessToast("Lead source updated successfully");
    } catch (error) {
      console.error("Error updating source:", error);
      showErrorToast(error.response?.data || "Error updating lead source");
    }
  }

  const updateInterestTag = async (id, data) => {
    try {
      await InterestedTagService.updateInterestedTag(id, data.trim());
      await fetchInterestTagData();
      showSuccessToast("Interest tag updated successfully");
    } catch (error) {
      console.error("Error updating interest tag:", error);
      showErrorToast(error.response?.data || "Error updating interest tag");
    }
  }

  // File handling for bulk upload 

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    const validFileTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];

    if (file && !validFileTypes.includes(file.type)) {
      showErrorToast('Invalid file type. Only .xlsx and .csv files are allowed.');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = xlsx.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet);
        const names = new Set();



        for (let i = 0; i < json.length; i++) {
          if (!json[i]["Name"]) {
            showErrorToast("Missing name in one or more entries");
            setSelectedFile(null);
            return;
          }
          

          
          const name = String(json[i]["Name"]).trim();
          if (name.length === 0) {
            showErrorToast("Name cannot be empty");
            setSelectedFile(null);
            return;
          }
          if (name.length > 50) {
            showErrorToast("Name cannot exceed 50 characters.");
            setSelectedFile(null);
            return;
          }
          
          if (names.has(name)) {
            showErrorToast("Duplicate names found in the file. Please correct and try again.");
            setSelectedFile(null);
            return;
          } else {
            names.add(name);
          }
        }
      
        const currentCount = modalHeading === "Interest Tags" ? counts.interestTag :
                            modalHeading === "Lead Sources" ? counts.leadSource :
                            modalHeading === "Location" ? counts.location : 0;
                            
        const limit = modalHeading === "Interest Tags" ? tagLimits.interestTag :
                     modalHeading === "Lead Sources" ? tagLimits.leadSource :
                     modalHeading === "Location" ? tagLimits.location : 0;
                     
        if (currentCount + json.length > limit) {
          showErrorToast(`Uploading these items would exceed the maximum limit of ${limit}`);
          setSelectedFile(null);
          return;
        }
        
        const inputJson = json.map(item => ({
          name: String(item["Name"]).trim(),
        }));
        
        setInputJson(inputJson);
      };
      reader.readAsArrayBuffer(file);
    }
  }, [modalHeading, counts, tagLimits]);

  // Bulk upload
  const handleBulkUpload = useCallback(async () => {
    try {
      if (!selectedFile || !inputJson) {
        showErrorToast("Please select a file before uploading.");
        return;
      }
      
      const fileObj = { items: inputJson };
    const response =  await InterestedTagService.bulkUploadInterestAndsources(fileObj, modalHeading);
    console.log(response)
    setShowSkippedModal(true)
    setInsertedCount(response?.data?.insertedCount)
    setSkippedUnitsInfo({
      skippedCount: response.data.skippedCount,
      skippedUnitNos: response.data.skippedNames || []
    });
      closeModal();
   
        if (modalHeading === "Interest Tags") {
            await fetchInterestTagData();
        } else if (modalHeading === "Lead Sources") {
            await fetchSourceData();
        } else if (modalHeading === "Location") {
            await fetchInterestLocationData();
        }
      showSuccessToast("Bulk Upload completed successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      showErrorToast("Error uploading file. Please try again.");
    }
  }, [selectedFile, inputJson, modalHeading, closeModal]);

  
  const handleDownloadTemplate = useCallback(() => {
    const link = document.createElement("a");
    link.href = "/Sample Interest and Sources.xlsx";
    link.setAttribute("download", "Sample-template Interest and Sources.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return {
    
    interestLocationData,
    sourceData,
    interestTagData,
    loading,
    
    menuOpen,
    setMenuOpen,
    isModalOpen,
    modalHeading,
    selectedFile,
  
    tagLimits,
    counts,
    limitsExceeded,
    
    dropdownRef,

    toggleMenu,
    openModal,
    closeModal,
    
    fetchInterestLocationData,
    fetchSourceData,
    fetchInterestTagData,

    createInterestLocation,
    createSource,
    createInterestTag,
    updateInterestLocation,
    updateSource,
    updateInterestTag,
    
    handleFileChange,
    handleBulkUpload,
    handleDownloadTemplate,
    insertedCount,
    skippedUnitsInfo,
    showSkippedModal,
    setShowSkippedModal
  };
};

export default useInterestAndSource;